import { useEffect, useRef } from 'react'

/**
 * Merges rapid per-id patches and flushes each id's merged patch after `delay` of
 * inactivity. On unmount, every still-pending id is flushed immediately instead of
 * discarded, so a fast navigation away never silently drops an in-flight edit.
 */
export default function useDebouncedPatchQueue(delay = 800) {
    const mapRef = useRef(new Map())

    const enqueue = (id, patch, flush) => {
        const existing = mapRef.current.get(id)
        if (existing) clearTimeout(existing.timeoutId)
        const pending = { ...(existing?.pending || {}), ...patch }
        const timeoutId = setTimeout(() => {
            mapRef.current.delete(id)
            flush(pending)
        }, delay)
        mapRef.current.set(id, { timeoutId, pending, flush })
    }

    useEffect(
        () => () => {
            mapRef.current.forEach(({ timeoutId, pending, flush }) => {
                clearTimeout(timeoutId)
                flush(pending)
            })
            mapRef.current.clear()
        },
        []
    )

    return enqueue
}
